import type { ReactNode } from 'react';
import { useCaptchaConfig } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const PROVIDER_OPTIONS = [
  { value: 'disabled', label: '禁用' },
  { value: 'auto', label: '自动优先阿里云' },
  { value: 'aliyun', label: '强制阿里云 Captcha 2.0' },
  { value: 'tencent', label: '强制腾讯云 Captcha' },
  { value: 'self_hosted', label: '自建点选验证' },
] as const;

export function CaptchaSettingsPage() {
  const {
    data,
    form,
    loading,
    saving,
    deletingProvider,
    error,
    setProvider,
    setTaskClaimProvider,
    updateAliyunField,
    updateTencentField,
    save,
    removeProvider,
    reload,
  } = useCaptchaConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          管理注册、登录、提现等链路的人机验证 provider。保存后无需重启，下一次打开验证组件或下一次服务端校验会立即使用最新配置。
        </p>
        <p className="text-sm text-gray-500">
          领取任务可独立选择腾讯云或自建点选验证。切换前请先发布支持自建点选的 App；旧版本不支持自建题目。
        </p>
        {data?.using_env_fallback && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            当前尚未写入数据库运行时配置，页面展示的是环境变量基线。首次保存后，后续人机验证将优先使用后台保存的配置。
          </div>
        )}
      </div>

      <section className="border-b border-gray-200 pb-6 space-y-3">
        <h2 className="text-base font-medium text-gray-900">领取任务验证</h2>
        <p className="text-sm text-gray-500">当前生效：{data?.task_claim_provider === 'self_hosted' ? '自建点选' : '腾讯云'}</p>
        <label htmlFor="task-claim-provider" className="block text-sm font-medium text-gray-700">验证方式</label>
        <select id="task-claim-provider" value={form.task_claim_provider}
          onChange={(e) => setTaskClaimProvider(e.target.value as typeof form.task_claim_provider)}
          className="h-10 w-full max-w-md rounded-md border border-gray-300 px-3 text-sm">
          <option value="tencent">腾讯云点选验证</option>
          <option value="self_hosted">自建点选验证</option>
        </select>
        <p className="text-sm text-gray-500">自建题目有效期为 2 分钟，仅可提交一次；按提示顺序点击 3 个字符。无需填写第三方密钥。</p>
      </section>
      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">当前验证策略</h2>
          <p className="text-sm text-gray-500 mt-1">
            当前生效 provider：<span className="font-medium text-gray-700">{renderActiveProvider(data?.active_provider)}</span>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            `auto` 会优先尝试阿里云，阿里云未配置完整时再切到腾讯云。选择自建前，请先发布支持登录、注册和提现自建验证的新版 App。
          </p>
        </div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">人机验证 provider</label>
          <select
            value={form.provider}
            onChange={(e) => setProvider(e.target.value as typeof form.provider)}
            className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {PROVIDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-gray-900">阿里云 Captcha 2.0</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前状态：{data?.aliyun.ready ? '配置完整，可用于校验' : '配置未完成'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => removeProvider('aliyun')}
            disabled={deletingProvider === 'aliyun'}
            className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50"
          >
            {deletingProvider === 'aliyun' ? '删除中...' : '删除阿里云配置'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="AccessKeyId">
            <input
              type="text"
              value={form.aliyun.access_key_id}
              onChange={(e) => updateAliyunField('access_key_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="AccessKeySecret">
            <input
              type="password"
              value={form.aliyun.access_key_secret}
              onChange={(e) => updateAliyunField('access_key_secret', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Region">
            <input
              type="text"
              value={form.aliyun.region}
              onChange={(e) => updateAliyunField('region', e.target.value)}
              className={inputCls}
              placeholder="ap-southeast-1"
            />
          </Field>
          <Field label="Endpoint">
            <input
              type="text"
              value={form.aliyun.endpoint}
              onChange={(e) => updateAliyunField('endpoint', e.target.value)}
              className={inputCls}
              placeholder="captcha.ap-southeast-1.aliyuncs.com"
            />
          </Field>
          <Field label="SceneId">
            <input
              type="text"
              value={form.aliyun.scene_id}
              onChange={(e) => updateAliyunField('scene_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Prefix">
            <input
              type="text"
              value={form.aliyun.prefix}
              onChange={(e) => updateAliyunField('prefix', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-gray-900">腾讯云 Captcha</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前状态：{data?.tencent.ready ? '配置完整，可用于校验' : '配置未完成'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              领取任务选择腾讯云时使用这组配置；请在腾讯云控制台将对应实例设置为点选。
            </p>
          </div>
          <button
            type="button"
            onClick={() => removeProvider('tencent')}
            disabled={deletingProvider === 'tencent'}
            className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50"
          >
            {deletingProvider === 'tencent' ? '删除中...' : '删除腾讯云配置'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SecretId">
            <input
              type="text"
              value={form.tencent.secret_id}
              onChange={(e) => updateTencentField('secret_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="SecretKey">
            <input
              type="password"
              value={form.tencent.secret_key}
              onChange={(e) => updateTencentField('secret_key', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="CaptchaAppId">
            <input
              type="number"
              min={0}
              value={form.tencent.captcha_app_id}
              onChange={(e) => updateTencentField('captcha_app_id', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="AppSecretKey">
            <input
              type="password"
              value={form.tencent.app_secret_key}
              onChange={(e) => updateTencentField('app_secret_key', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="ClientJSURL">
            <input
              type="text"
              value={form.tencent.client_js_url}
              onChange={(e) => updateTencentField('client_js_url', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-500">
          <div>最近更新时间：{data?.updated_at ? formatDateTime(data.updated_at) : '未保存到数据库'}</div>
          <div>最近更新人：{data?.updated_by ?? '-'}</div>
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary/90 disabled:opacity-60"
        >
          {saving ? '保存中...' : '保存并立即生效'}
        </button>
      </section>
    </div>
  );
}

function renderActiveProvider(provider?: string) {
  switch (provider) {
    case 'aliyun':
      return '阿里云';
    case 'tencent':
      return '腾讯云';
    case 'self_hosted':
      return '自建点选';
    default:
      return '禁用';
  }
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
